import BaseballResultsProcessor from '../entities/BaseballResultsProcessor.js';
import buildFiltersForPitchingSummary from '../filters/pitchingSummaryFilters.js';
import {
    hitClassifier, homeRunClassifier, contactClassifier, fairBallClassifier, strikeOutClassifier
} from '../classificators/index.js';
import buildFiltersForHittingSummary from '../filters/hittingSummaryFilters.js';
import { filterOnlyHavingParameterSet, filterOnlyMatchingPredicate } from '../filters/byParameterName.js';
import {
    HitReprocessed, HitData, BattedBallType, Handedness, PitchResult,
    PitchType, PitchData, ResultData, PitchReprocessed
} from '../enums/index.js';
import StringifiedParameterFormatter from '../formatters/StringifiedParameterFormatter.js';
import UniqueParametersForPitchAndHitProvider from './UniqueParametersForPitchAndHitProvider.js';
import { filterOnlyPitches } from '../filters/byResultTypes.js';

// see https://docs.google.com/document/d/1GCQVVh6Ue0DrSiaHLYZ4zasDBxZSEi6kYt1aHgD_afo/edit#heading=h.j2le5wcsz4th
const homeRunFilter = filterOnlyMatchingPredicate(
    ResultData.getDataPath(PitchData.Result.key), homeRunClassifier
);
const hitFilter = filterOnlyMatchingPredicate(
    ResultData.getDataPath(PitchData.Result.key), hitClassifier
);

const contactFilter = filterOnlyMatchingPredicate(
    ResultData.getDataPath(PitchData.Result.key), contactClassifier
);

const fairBallFilter = filterOnlyMatchingPredicate(
    ResultData.getDataPath(PitchData.Result.key), fairBallClassifier
);

const strikeoutFilter = filterOnlyMatchingPredicate(
    ResultData.getDataPath(PitchData.Result.key), strikeOutClassifier
);

const buildPitchesFiltersArray = function({
    // tagging context
    pitcherIds, pitcherHandedness, pitchResults, pitchTypes, batterIds, batterHandedness,
    // scoring context
    outs, balls, strikes, runnersOn,
}){
    let filtersForPitches = buildFiltersForPitchingSummary({
        pitcherIds, pitcherHandedness, pitchResults, pitchTypes, batterIds, batterHandedness,
        outs, balls, strikes, runnersOn,
    });

    filtersForPitches = filtersForPitches.concat(
        filterOnlyHavingParameterSet(ResultData.BatterTagId.key)
    );

    return filtersForPitches;
};

const buildHitsFiltersArray = function({ batterIds, batterHandedness, pitcherHandedness }){
    return buildFiltersForHittingSummary(
        { batterIds, batterHandedness, pitcherHandedness }
    );
};

const mapSinglePitch = function(pitchResult, resultsProcessor){
    if (typeof(pitchResult) !== 'object' || pitchResult === null){
        return;
    }

    return resultsProcessor.mapCollectionProperties([pitchResult], [
        [ResultData.Id.key],
        [ResultData.TagId.key],
        [ResultData.BatterTagId.key],
        ResultData.getDataPath(PitchData.Speed.key),
        ResultData.getDataPath(PitchData.Spin.key),
        //ResultData.getDataPath(PitchData.PFXX.key),
        ResultData.getDataPath(PitchData.Classification.key),
        ResultData.getDataPath(PitchData.Result.key),
        ResultData.getDataPath(PitchData.Handedness.key),
        ResultData.getDataPath(PitchData.SwingHandedness.key),
        ResultData.getDataPath(PitchReprocessed.StrikeZonePositionR.key),
    ])[0] || undefined;
};

const mapSingleHit = function(hitResult, resultsProcessor){
    if (typeof(hitResult) !== 'object' || hitResult === null){
        return;
    }

    return resultsProcessor.mapCollectionProperties([hitResult], [
        [ResultData.Id.key],
        [ResultData.TagId.key],
        ResultData.getDataPath(HitData.SwingHandedness.key),
        ResultData.getDataPath(HitData.ExitSpeed.key),
        ResultData.getDataPath(HitData.Spin.key),
        ResultData.getDataPath(HitData.CarryDistance.key),
        ResultData.getDataPath(HitData.LaunchH.key),
        ResultData.getDataPath(HitData.LaunchV.key),
        ResultData.getDataPath(HitData.FlightTime.key),
        ResultData.getDataPath(HitData.LandingPoint.key),
        ResultData.getDataPath(HitReprocessed.BattedBallTypeR.key),
    ])[0] || undefined;
};

const determineSummaryForBatter = function(batterId, pitchesAgainstBatter, hitsOfBatter, resultsProcessor){
    const finalPitchesForBatter = filterFinalActionForPa(
        pitchesAgainstBatter, ResultData.getDataPath(PitchReprocessed.PlateAppearanceR.key)
    );
    const finalHitsForBatter = filterFinalActionForPa(
        hitsOfBatter, ResultData.getDataPath(HitReprocessed.PlateAppearanceR.key)
    );

    const pitchesPerPa = resultsProcessor.groupData(
        finalPitchesForBatter, [ResultData.getDataPath(PitchReprocessed.PlateAppearanceR.key)]
    );
    const hitsPerPa = resultsProcessor.groupData(
        finalHitsForBatter, [ResultData.getDataPath(HitReprocessed.PlateAppearanceR.key)]
    );

    const allPitchesPerPa = resultsProcessor.groupData(
        pitchesAgainstBatter, [ResultData.getDataPath(PitchReprocessed.PlateAppearanceR.key)]
    );
    const allHitsPerPa = resultsProcessor.groupData(
        hitsOfBatter, [ResultData.getDataPath(HitReprocessed.PlateAppearanceR.key)]
    );

    const summaryForBatter = {
        batterId,
        plateAppearances: determineSummaryForBatterPerPa(pitchesPerPa, hitsPerPa, allPitchesPerPa, resultsProcessor) || [],
        summary: determineSummaryForAllPas(allPitchesPerPa, allHitsPerPa, resultsProcessor)
    };

    return summaryForBatter;
};

// TODO - separate class / provider
const determineSummaryForAllPas = function(pitchesPerPa, hitsPerPa, resultsProcessor){
    const summary = {
        Hits: 0,
        HomeRuns: 0,
        Strikeouts: 0,
        TotalBases: 0,
        GroundBalls: 0,
        Pitches: 0,
        BattedBalls: 0,
        FairBalls: 0,
        LineDrives: 0,
        FlyBalls: 0,
        PopUps: 0,
        PlateAppearances: 0,
        GroundBallRate: NaN,
        LineDriveRate: NaN,
        FlyBallRate: NaN,
        PopUpRate: NaN,
        PitchesPerPa: NaN
    };
    const allPas = Object.keys(pitchesPerPa).concat(Object.keys(hitsPerPa));
    const processedPas = [];

    for (let i = 0; i < allPas.length; i++) {
        const paNr = allPas[i];

        if (processedPas.indexOf(paNr) > -1){
            continue;
        }

        processedPas.push(paNr);

        const hitsInPa = hitsPerPa[paNr] || [];
        const pitchesInPa = pitchesPerPa[paNr] || [];
        
        summary.PlateAppearances ++;

        if (pitchesInPa.length) {
            const hits = resultsProcessor.filterData(pitchesInPa, hitFilter);
            summary.Pitches += pitchesInPa.length;
            summary.HomeRuns += resultsProcessor.filterData(pitchesInPa, homeRunFilter).length;
            summary.Strikeouts += resultsProcessor.filterData(pitchesInPa, strikeoutFilter).length;
            summary.Hits += hits.length;
            summary.BattedBalls += resultsProcessor.filterData(pitchesInPa, contactFilter).length;
            for (let i = 0; i < hits.length; i++){
                const hit = hits[i];
                summary.TotalBases += determineNrOfBases(hit);
            }
    
            if (!hitsInPa || !hitsInPa.length){
                continue;
            }
    
            const fairBalls = resultsProcessor.filterData(pitchesInPa, fairBallFilter);
            const fairBallsWithHits = BaseballResultsProcessor.matchPitchesAndHits(
                fairBalls.concat(hitsInPa)
            );
    
            const fairHitsWithTaggedPitches = resultsProcessor.filterData(
                fairBallsWithHits, filterOnlyHavingParameterSet('hit').concat(
                    filterOnlyHavingParameterSet('pitch')
                )
            );
    
            summary.FairBalls += fairHitsWithTaggedPitches.length;
            const mappedFairHitsTagged = [];
            for (let i = 0; i < fairHitsWithTaggedPitches.length; i++) {
                const singlePair = fairHitsWithTaggedPitches[i];
                if (singlePair && singlePair.hit){
                    mappedFairHitsTagged.push(singlePair.hit);
                }
            }

            calculateCountsOfBattedBallTypes(summary, mappedFairHitsTagged);
        }
         // hits only case...
        else if(hitsInPa.length){
            summary.BattedBalls += hitsInPa.length;
            summary.FairBalls += hitsInPa.length;

            calculateCountsOfBattedBallTypes(summary, hitsInPa);
        }
    }

    calculateBattedBallTypeRates(summary);

    if (summary.PlateAppearances){
        summary.PitchesPerPa = summary.Pitches / summary.PlateAppearances;
    }

    return summary;
};

const calculateBattedBallTypeRates = function(battersSummary){
    if (battersSummary.FairBalls){
        //http://m.mlb.com/glossary/advanced-stats/ground-ball-rate
        battersSummary.GroundBallRate = 100 * battersSummary.GroundBalls / battersSummary.FairBalls;

        // http://m.mlb.com/glossary/advanced-stats/line-drive-rate
        battersSummary.LineDriveRate = 100 * battersSummary.LineDrives / battersSummary.FairBalls;

        // https://www.baseball-reference.com/bullpen/Fly_ball
        // http://m.mlb.com/glossary/advanced-stats/fly-ball-rate
        battersSummary.FlyBallRate = 100 * battersSummary.FlyBalls / battersSummary.FairBalls;

        // http://m.mlb.com/glossary/advanced-stats/pop-up-rate
        battersSummary.PopUpRate = 100 * battersSummary.PopUps / battersSummary.FairBalls;
    }

    return battersSummary;
};

// TODO - separate class / provider
const calculateCountsOfBattedBallTypes = function(battersSummary, hitResultsTaggedAsFairBalls){
    for (let j = 0; j < hitResultsTaggedAsFairBalls.length; j++) {
        const singleHit = hitResultsTaggedAsFairBalls[j];
        const battedBallType = BaseballResultsProcessor.getObjectContent(singleHit, ResultData.getDataPath(HitReprocessed.BattedBallTypeR.key));

        switch (battedBallType) {
            case BattedBallType.GroundBall.key:
                battersSummary.GroundBalls ++;
                break;

            case BattedBallType.LineDrive.key:
                battersSummary.LineDrives ++;
                break;

            case BattedBallType.FlyBall.key:
                battersSummary.FlyBalls ++;
                break;

            case BattedBallType.PopUp.key:
                battersSummary.PopUps ++;
                break;
            default:
                // TODO - consider summary.FairBalls --;
                break;
        }
    }

    return battersSummary;
};

// TODO - separate class / provider
const determineNrOfBases = function(result){
    let nrOfBases = 0;
    const pitchResult = BaseballResultsProcessor.getObjectContent(
        result, ResultData.getDataPath(PitchData.Result.key)
    );

    if (!pitchResult){
        return nrOfBases;
    }

    switch (pitchResult) {
        case PitchResult.Single.key:
            nrOfBases = 1;
            break;
        
        case PitchResult.Double.key:
            nrOfBases = 2;
            break;

        case PitchResult.Triple.key:
            nrOfBases = 3;
            break;

        case PitchResult.HomeRun.key:
            nrOfBases = 4;
            break;

        default:
            break;
    }

    return nrOfBases;
};

const determineSummaryForBatterPerPa = function(finalPitchPerPa, finalHitPerPa, allPitcherPerPa, resultsProcessor){
    const summaryPerPa = [];
    const allPas = Object.keys(finalPitchPerPa).concat(Object.keys(finalHitPerPa));
    const processedPas = [];

    for (let index = 0; index < allPas.length; index++) {
        const paNr = allPas[index];

        if (processedPas.indexOf(paNr) > -1){
            continue;
        }

        processedPas.push(paNr);

        const finalPitchInPa = finalPitchPerPa[paNr] ? finalPitchPerPa[paNr][0] : undefined;
        const allPitchesInPa = allPitcherPerPa[paNr] ? allPitcherPerPa[paNr]: [];
        let finalHitInPa = finalHitPerPa[paNr] ? finalHitPerPa[paNr][0] : undefined;

        if (finalPitchInPa && finalHitInPa && finalPitchInPa[ResultData.HitId.key] !== finalHitInPa[ResultData.Id.key]){
            // TODO - log, final pitch is not matching final hit
            finalHitInPa = undefined;
        }

        if (!finalPitchInPa && finalHitInPa && finalHitInPa[ResultData.PitchId.key]){
            // so we have no pitch related to this hit in filtered set, but it does exist in full set
            continue;
        }

        summaryPerPa.push(
            buildSummaryForPa(finalPitchInPa, finalHitInPa, allPitchesInPa, paNr, resultsProcessor)
        );
    }

    return summaryPerPa;
};

const buildSummaryForPa = function(finalPitchInPa, finalHitInPa, allPitchesInPa, paNr, resultsProcessor) {
    const plateAppearance = parseInt(paNr, 10);

    if (!plateAppearance){
        throw new TypeError('paNr must be numeric and greater than 0');
    }

    const summaryForPa = {
        PlateAppearance: plateAppearance,
        // stuff related with pitch
        FinalPitchId: 0,
        NumberOfPitches: 0,
        PitcherId: 0,
        PitchThrown: Handedness.Unidentified.key,
        PitchResult: PitchResult.Unidentified.key,
        PitchType: PitchType.Unidentified.key,
        PitchSpeed: NaN,
        PitchSpin: NaN,
        StrikeZoneSector: null,

        // stuff related with hit
        BattedBallType: BattedBallType.Unidentified.key,
        FinalHitId: 0,
        BatterId: 0,
        SwingHandedness: Handedness.Unidentified.key,
        ExitSpeed: NaN,
        CarryDistance: NaN,
        HangTime: NaN,
        HitLaunchH: NaN,
        HitLaunchV: NaN,
        LandingPoint: {
            X: NaN,
            Y: NaN,
            Z: NaN
        }
    };

    summaryForPa.NumberOfPitches = allPitchesInPa.length;

    if (typeof(finalPitchInPa) === 'object'){
        try {
            const pitchMapped = mapSinglePitch(finalPitchInPa, resultsProcessor);
            summaryForPa.FinalPitchId = pitchMapped[ResultData.Id.key] || 0;
            summaryForPa.PitcherId = pitchMapped[ResultData.TagId.key] || 0;
            summaryForPa.PitchThrown = 
                pitchMapped[PitchData.Handedness.key] || Handedness.Unidentified.key;
            summaryForPa.PitchResult =
                pitchMapped[PitchData.Result.key] || PitchResult.Unidentified.key;
            summaryForPa.PitchType =
                pitchMapped[PitchData.Classification.key] || PitchType.Unidentified.key;
            summaryForPa.PitchSpeed = pitchMapped[PitchData.Speed.key];
            summaryForPa.PitchSpin = pitchMapped[PitchData.Spin.key];
            summaryForPa.StrikeZoneSector = pitchMapped[PitchReprocessed.StrikeZonePositionR.key];
    
            summaryForPa.BatterId = pitchMapped[ResultData.BatterTagId.key];
            summaryForPa.SwingHandedness =
                pitchMapped[PitchData.SwingHandedness.key] || Handedness.Unidentified.key;
        } catch (error) {
            // TODO - log ?
        }
    }

    if (typeof(finalHitInPa) === 'object'){
        try {
            const hitMapped = mapSingleHit(finalHitInPa, resultsProcessor);
            summaryForPa.FinalHitId = hitMapped[ResultData.Id.key] || 0;
            summaryForPa.BatterId = hitMapped[ResultData.TagId.key] || 0;
            summaryForPa.BattedBallType =
                hitMapped[HitReprocessed.BattedBallTypeR.key] || BattedBallType.Unidentified.key;
            summaryForPa.SwingHandedness =
                hitMapped[HitData.SwingHandedness.key] || Handedness.Unidentified.key;
            summaryForPa.ExitSpeed = hitMapped[HitData.ExitSpeed.key];
            summaryForPa.CarryDistance = hitMapped[HitData.CarryDistance.key];
            summaryForPa.HangTime = hitMapped[HitData.FlightTime.key];
            summaryForPa.HitLaunchH = hitMapped[HitData.LaunchH.key];
            summaryForPa.HitLaunchV = hitMapped[HitData.LaunchV.key];
    
            let formattedLocation = StringifiedParameterFormatter(
                hitMapped[HitData.LandingPoint.key], ';', HitData.LandingPoint.stringifiedAs, parseFloat
            );
            if (formattedLocation){
                summaryForPa.LandingPoint = Object.assign(summaryForPa.LandingPoint, formattedLocation);
            }
        } catch (error) {
            // TODO - log?
        }
    }

    return summaryForPa;
};

const filterFinalActionForPa = function(results, paParameterPath){
    const finalResults = [];

    for (let index = 1; index < results.length; index++) {
        const currResult = results[index];
        const currPa = BaseballResultsProcessor.getObjectContent(
            currResult, paParameterPath
        );

        if (!currPa){
            continue;
        }

        const prevResult = results[index-1];
        const prevPa = BaseballResultsProcessor.getObjectContent(
            prevResult, paParameterPath
        );

        if (currPa != prevPa){
            finalResults.push(prevResult);
        }
    }

    if (results.length > 0){
        const lastResult = results[results.length - 1];
        const lastPa = BaseballResultsProcessor.getObjectContent(
            lastResult, paParameterPath
        );

        if (lastPa){
            finalResults.push(lastResult);
        }
    }

    return finalResults;
};

const getDataForHittingSummary = function(
    results, customResultsProcessor,
    {
        // tagging context
        pitcherIds, pitcherHandedness, pitchResults, pitchTypes, batterIds, batterHandedness,
        // scoring context
        outs, balls, strikes, runnersOn,
    } = {}
){
    let resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();
    const pitchingFilters = buildPitchesFiltersArray({
        pitcherIds, pitcherHandedness, pitchResults, pitchTypes, batterIds, batterHandedness,
        outs, balls, strikes, runnersOn,
    });
    const hittingFilters = buildHitsFiltersArray({ batterIds, batterHandedness, pitcherHandedness });
    const filteredPitches = resultsProcessor.filterData(results, pitchingFilters);
    const allPitches = resultsProcessor.filterData(results, filterOnlyPitches());
    const preFilteredHits = resultsProcessor.filterData(results, hittingFilters);

    // now we need to get only hits without matching pitch (using allPitches) and hits with matching pitch from filteredPitches
    const filteredHitsMatchingToAllPitches = BaseballResultsProcessor.matchPitchesAndHits(preFilteredHits.concat(allPitches));
    const filteredHitsWithoutMatchingPitch = resultsProcessor.filterData(filteredHitsMatchingToAllPitches, [
        { parameter: ['pitch'], operator: 'empty', value: null },
        { parameter: ['hit'], operator: 'notEmpty', value: null },
    ]);

    const filteredHitsMatchingToFilteredPitches = BaseballResultsProcessor.matchPitchesAndHits(preFilteredHits.concat(filteredPitches));
    const filteredHitsWithMatchingFilteredPitch = resultsProcessor.filterData(filteredHitsMatchingToFilteredPitches, [
        { parameter: ['pitch'], operator: 'notEmpty', value: null },
        { parameter: ['hit'], operator: 'notEmpty', value: null },
    ]);

    const filteredPairs = filteredHitsWithoutMatchingPitch.concat(filteredHitsWithMatchingFilteredPitch);
    const filteredHits = filteredPairs.map(pair => pair.hit);

    const pitchesPerBatter = resultsProcessor.groupData(filteredPitches, [ResultData.BatterTagId.key]);
    const hitsPerBatter = resultsProcessor.groupData(filteredHits, [ResultData.TagId.key]);
    const summaryPerHitter = [];
    const allBatters = Object.keys(pitchesPerBatter).concat(Object.keys(hitsPerBatter));

    // now we must match it...
    // TODO - what about order ? rara case but still possible
    const processedBatters = [];
    for (let i = 0; i < allBatters.length; i++) {
        const batterId = allBatters[i];

        if (processedBatters.indexOf(batterId) > -1){
            continue;
        }

        processedBatters.push(batterId);

        const pitchesAgainstBatter = pitchesPerBatter[batterId] || [];
        const hitsOfBatter = hitsPerBatter[batterId] || [];

        const summaryForHitter = determineSummaryForBatter(
            batterId, pitchesAgainstBatter, hitsOfBatter, resultsProcessor
        );

        if (!summaryForHitter.plateAppearances || summaryForHitter.plateAppearances.length === 0){
            // because of complex filtering we need to skip batters with no pa's recorded for given filters
            continue;
        }
        
        summaryPerHitter.push(summaryForHitter);
    }

    return summaryPerHitter;
};

const listUniqueParametersForHittingSummary = function(
    results, customResultsProcessor, {
        // tagging context
        pitcherIds, pitcherHandedness, pitchResults, pitchTypes, batterIds, batterHandedness,
        // scoring context
        outs, balls, strikes, runnersOn,
    } = {},
    pitchParameters, hitParameters, pitchToHitParamsToMerge, combinedPitchResult
){
    pitchParameters = pitchParameters || [
        // tagging context
        [ResultData.BatterTagId.key],
        [ResultData.TagId.key],
        ResultData.getDataPath(PitchData.SwingHandedness.key),
        ResultData.getDataPath(PitchData.Result.key),
        ResultData.getDataPath(PitchData.Classification.key),
        //ResultData.getDataPath(PitchData.Mechanics.key),
        ResultData.getDataPath(PitchData.Handedness.key),
        // scoring context
        ResultData.getDataPath(PitchData.BeforePlayOuts.key),
        ResultData.getDataPath(PitchData.BeforePlayBalls.key),
        ResultData.getDataPath(PitchData.BeforePlayStrikes.key),
        ResultData.getDataPath(PitchData.BeforePlayRunnersOn.key),
    ];

    hitParameters = hitParameters || [
        [ResultData.TagId.key],
        ResultData.getDataPath(HitData.SwingHandedness.key),
        // ResultData.getDataPath(PitchData.Handedness.key), // right now we can't use it
    ];

    pitchToHitParamsToMerge = pitchToHitParamsToMerge || [
        0, undefined, 1
    ];

    let resultsProcessor = customResultsProcessor || new BaseballResultsProcessor();
    const pitchingFilters = buildPitchesFiltersArray({
        pitcherIds, pitcherHandedness, pitchResults, pitchTypes, batterIds, batterHandedness,
        outs, balls, strikes, runnersOn,
    });
    const hittingFilters = buildHitsFiltersArray({ batterIds, batterHandedness, pitcherHandedness });
    const filteredPitches = resultsProcessor.filterData(results, pitchingFilters);
    const allPitches = resultsProcessor.filterData(results, filterOnlyPitches());
    const preFilteredHits = resultsProcessor.filterData(results, hittingFilters);

    // now we need to get only hits without matching pitch (using allPitches) and hits with matching pitch from filteredPitches
    const filteredHitsMatchingToAllPitches = BaseballResultsProcessor.matchPitchesAndHits(preFilteredHits.concat(allPitches));
    const filteredHitsWithoutMatchingPitch = resultsProcessor.filterData(filteredHitsMatchingToAllPitches, [
        { parameter: ['pitch'], operator: 'empty', value: null },
        { parameter: ['hit'], operator: 'notEmpty', value: null },
    ]);

    const filteredHitsMatchingToFilteredPitches = BaseballResultsProcessor.matchPitchesAndHits(preFilteredHits.concat(filteredPitches));
    const filteredHitsWithMatchingFilteredPitch = resultsProcessor.filterData(filteredHitsMatchingToFilteredPitches, [
        { parameter: ['pitch'], operator: 'notEmpty', value: null },
        { parameter: ['hit'], operator: 'notEmpty', value: null },
    ]);

    const filteredPairs = filteredHitsWithoutMatchingPitch.concat(filteredHitsWithMatchingFilteredPitch);
    const filteredHits = filteredPairs.map(pair => pair.hit);
    
    return UniqueParametersForPitchAndHitProvider(
        pitchParameters, hitParameters, pitchToHitParamsToMerge, filteredPitches, filteredHits, resultsProcessor, combinedPitchResult
    );
};

export default getDataForHittingSummary;

export {
    getDataForHittingSummary, listUniqueParametersForHittingSummary,
};